"use client";

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSession } from "next-auth/react";
import {
  Star,
  Sparkles,
  Check,
  Copy,
  Tag,
  Wand2,
  RefreshCw,
  Zap,
  ShieldCheck,
  CheckCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Icons } from "@/components/shared/icons";
import { getProductNames } from "@/actions/products";
import { createReview } from "@/actions/reviews";

interface ReviewModalProps {
  showReviewModal: boolean;
  setShowReviewModal: Dispatch<SetStateAction<boolean>> | ((open: boolean) => void);
  defaultProduct?: string;
}

function ReviewModalContent({
  onClose,
  defaultProduct,
}: {
  onClose: () => void;
  defaultProduct?: string;
}) {
  const { data: session } = useSession();
  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [productOptions, setProductOptions] = useState<string[]>([]);
  const [product, setProduct] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [review, setReview] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [isAiGenerating, setIsAiGenerating] = useState<boolean>(false);
  const [aiActionName, setAiActionName] = useState<string>("");

  // Fetch available products from database
  useEffect(() => {
    let active = true;
    getProductNames()
      .then((items) => {
        if (active && items && items.length > 0) {
          const names = items.map((p) => p.name);
          setProductOptions(names);
          const storedDefault =
            typeof window !== "undefined"
              ? sessionStorage.getItem("review_default_product")
              : null;
          const target = defaultProduct || storedDefault;
          const match = target
            ? names.find((n) => n.toLowerCase() === target.toLowerCase())
            : undefined;

          setProduct((current) => {
            if (current && names.includes(current)) return current;
            return match || names[0];
          });
        }
      })
      .catch((err) => {
        console.error("Failed to load products for review modal:", err);
      });

    return () => {
      active = false;
    };
  }, [defaultProduct]);

  // Formatted display name from session
  const displayName =
    session?.user?.name ||
    (session?.user?.email ? session.user.email.split("@")[0] : "Player");

  // Call Google Gemini API route (with smart local fallback)
  const handleAiAction = async (action: "generate" | "polish" | "stealth" | "pro") => {
    setIsAiGenerating(true);
    setAiActionName(action);

    try {
      const res = await fetch("/api/ai/review-assist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          product: product || "Foulware Software",
          rating,
          currentText: review,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.text) {
          setReview(data.text);
          const actionLabels: Record<string, string> = {
            generate: "AI review generated!",
            polish: "Polished grammar & phrasing!",
            pro: "Enhanced with Pro gamer tone!",
            stealth: "Emphasized anticheat stealth!",
          };
          toast.success(actionLabels[action] || "Updated with AI!");
        }
      }
    } catch (err) {
      console.error("AI Assist failed:", err);
    } finally {
      setIsAiGenerating(false);
      setAiActionName("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!review.trim()) {
      toast.error("Please write a short review before submitting.");
      return;
    }

    setIsSubmitting(true);
    try {
      const helpertag = role.trim()
        ? `${role.trim()} at ${product || "Foulware"}`
        : `${product || "Foulware"} Verified Player`;

      await createReview({
        rating,
        displayName,
        helpertag,
        review,
        productName: product || undefined,
      });

      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("balance-updated"));
      }

      setIsSubmitted(true);
      toast.success("Review submitted successfully!", {
        description: "$10.00 credit has been added to your account balance.",
      });
    } catch (err: any) {
      console.error("Failed to submit review:", err);
      toast.error(err?.message || "Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center text-center space-y-4 py-2">
        <div className="flex size-12 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 shadow-sm">
          <Check className="size-6 stroke-[2.5]" />
        </div>

        <div className="space-y-1">
          <h3 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl font-urban">
            Thank you for your review!
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-sm">
            Your feedback helps us continuously improve our bypass algorithms and macros.
          </p>
        </div>

        {/* Account Credit Reward Box */}
        <div className="w-full rounded-2xl border border-dashed border-emerald-500/40 bg-emerald-500/5 dark:bg-emerald-500/10 p-4 sm:p-5 text-left space-y-2 mt-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              <Sparkles className="size-3.5" />
              <span>Reward Applied</span>
            </div>
            <span className="text-[11px] text-muted-foreground font-mono">Instant Balance</span>
          </div>

          <div className="flex items-center justify-between gap-3 bg-background border border-border/80 rounded-xl px-4 py-3 shadow-2xs">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shadow-2xs">
                <span className="font-bold text-base leading-none">$</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] text-muted-foreground font-medium">Account Credit</span>
                <span className="font-mono font-bold text-lg text-emerald-600 dark:text-emerald-400 tracking-tight">
                  +$10.00 Added
                </span>
              </div>
            </div>
            <div className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              <Check className="size-3.5 stroke-[2.5]" />
              <span>Credited</span>
            </div>
          </div>

          <p className="text-[11.5px] text-muted-foreground leading-relaxed pt-0.5">
            $10.00 credit has been added to your account credit and can be used on any purchase.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl h-11 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all shadow-xs flex items-center justify-center text-sm cursor-pointer mt-2"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left">
      {/* Header Greeting */}
      <div className="flex flex-col items-center space-y-2 text-center mb-1">
        <div className="flex size-10 items-center justify-center rounded-xl border border-border/80 bg-muted/40 text-foreground shadow-2xs">
          <Sparkles className="size-5 text-indigo-500" />
        </div>
        <div className="space-y-0.5">
          <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl font-urban">
            Hi {displayName} ! 👋
          </h2>
          <p className="text-xs sm:text-[13px] text-muted-foreground">
            Share your experience and claim $10.00 credit added to your balance.
          </p>
        </div>
      </div>

      {/* 5-Star Rating Selector */}
      <div className="flex flex-col items-center justify-center gap-1 py-1">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => {
            const isFilled = (hoverRating || rating) >= star;
            return (
              <button
                key={star}
                type="button"
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
                className="p-1 text-amber-500 transition-transform hover:scale-115 cursor-pointer focus:outline-none"
                aria-label={`Rate ${star} stars`}
              >
                <Star
                  className={cn(
                    "size-6 sm:size-7 transition-colors",
                    isFilled ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
                  )}
                />
              </button>
            );
          })}
        </div>
        <span className="text-[11px] font-medium text-muted-foreground">
          {rating === 5
            ? "5.0 - Excellent & Undetected"
            : rating === 4
            ? "4.0 - Very Good"
            : rating === 3
            ? "3.0 - Good"
            : `${rating}.0 Stars`}
        </span>
      </div>

      {/* Product Selection */}
      <div className="space-y-1.5">
        <Label htmlFor="review-product" className="text-xs font-semibold text-foreground">
          Product or Macro Used
        </Label>
        <Select value={product} onValueChange={setProduct}>
          <SelectTrigger id="review-product" className="h-10 rounded-xl text-xs sm:text-sm">
            <SelectValue placeholder="Select a product or macro" />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-border bg-popover z-50">
            {productOptions.length > 0 ? (
              productOptions.map((opt) => (
                <SelectItem key={opt} value={opt} className="rounded-lg text-xs sm:text-sm cursor-pointer">
                  {opt}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="General Software & Support" className="rounded-lg text-xs sm:text-sm cursor-pointer">
                General Software & Support
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Optional Game / Rank / Role Input */}
      <div className="space-y-1.5">
        <Label htmlFor="review-role" className="text-xs font-semibold text-foreground">
          Game / Rank (Optional)
        </Label>
        <Input
          id="review-role"
          placeholder="e.g. Faceit Lv10, PUBG Streamer, Predator #1"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-10 rounded-xl text-xs sm:text-sm"
        />
      </div>

      {/* Clean Review Textarea with Clean External AI Controls */}
      <div className="space-y-2">
        {/* Label Row with Clean AI Assistant Action */}
        <div className="flex items-center justify-between">
          <Label htmlFor="review-text" className="text-xs font-semibold text-foreground">
            Your Review
          </Label>

          {/* AI Helper Trigger */}
          <button
            type="button"
            disabled={isAiGenerating}
            onClick={() => handleAiAction(review.trim() ? "polish" : "generate")}
            className="group inline-flex items-center gap-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2.5 py-0.5 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 transition-all hover:bg-indigo-500/20 active:scale-95 cursor-pointer disabled:opacity-50"
          >
            {isAiGenerating ? (
              <RefreshCw className="size-3 animate-spin" />
            ) : (
              <Sparkles className="size-3 transition-transform group-hover:rotate-12" />
            )}
            <span>
              {isAiGenerating
                ? "Writing..."
                : review.trim()
                ? "Polish with AI"
                : "Auto-Draft with AI"}
            </span>
          </button>
        </div>

        {/* Clean Standard Textarea - No buttons inside */}
        <textarea
          id="review-text"
          rows={3}
          required
          placeholder="Tell us about recoil accuracy, ease of setup, key dispatch, and anticheat stealth..."
          value={review}
          onChange={(e) => setReview(e.target.value)}
          disabled={isAiGenerating}
          className="w-full rounded-xl border border-input bg-background p-3.5 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground shadow-2xs transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring resize-none min-h-[95px] disabled:opacity-60"
        />

        {/* External Tone Chips & Character Counter */}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          {/* Quick AI Tone Modifiers (only when text is present) */}
          <div className="flex flex-wrap items-center gap-1.5">
            {review.trim() ? (
              <>
                <button
                  type="button"
                  disabled={isAiGenerating}
                  onClick={() => handleAiAction("pro")}
                  className="inline-flex items-center gap-1 rounded-md bg-muted/60 hover:bg-muted px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-40"
                >
                  <Zap className="size-2.5 text-amber-500" />
                  <span>Pro Tone</span>
                </button>
                <button
                  type="button"
                  disabled={isAiGenerating}
                  onClick={() => handleAiAction("stealth")}
                  className="inline-flex items-center gap-1 rounded-md bg-muted/60 hover:bg-muted px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-40"
                >
                  <ShieldCheck className="size-2.5 text-sky-500" />
                  <span>Stealth Focus</span>
                </button>
                <button
                  type="button"
                  disabled={isAiGenerating}
                  onClick={() => handleAiAction("generate")}
                  className="inline-flex items-center gap-1 rounded-md bg-muted/60 hover:bg-muted px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-40"
                >
                  <RefreshCw className="size-2.5 text-indigo-500" />
                  <span>Rewrite</span>
                </button>
              </>
            ) : (
              <span className="text-[11px] text-muted-foreground/80">
                Tip: Click Auto-Draft for an instant sample review
              </span>
            )}
          </div>

          <span className="text-[10px] text-muted-foreground font-mono shrink-0">
            {review.length} chars
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting || isAiGenerating}
        className="w-full rounded-xl h-11 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all shadow-xs flex items-center justify-center gap-2 text-sm cursor-pointer disabled:opacity-50 mt-3"
      >
        {isSubmitting ? (
          <Icons.spinner className="size-4 animate-spin" />
        ) : (
          <span>Submit Review & Claim $10 Credit</span>
        )}
      </button>
    </form>
  );
}

export function ReviewModal({
  showReviewModal,
  setShowReviewModal,
  defaultProduct,
}: ReviewModalProps) {
  return (
    <Modal showModal={showReviewModal} setShowModal={setShowReviewModal} className="max-w-[480px]">
      <div className="w-full">
        <ReviewModalContent
          onClose={() => setShowReviewModal(false)}
          defaultProduct={defaultProduct}
        />
      </div>
    </Modal>
  );
}

export function useReviewModal() {
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [defaultProduct, setDefaultProduct] = useState<string | undefined>(undefined);

  const openReviewModal = useCallback((open: boolean, product?: string) => {
    setShowReviewModal(open);
    if (product !== undefined) {
      setDefaultProduct(product);
    }
  }, []);

  const ReviewModalCallback = useCallback(() => {
    return (
      <ReviewModal
        showReviewModal={showReviewModal}
        setShowReviewModal={setShowReviewModal}
        defaultProduct={defaultProduct}
      />
    );
  }, [showReviewModal, setShowReviewModal, defaultProduct]);

  return useMemo(
    () => ({
      setShowReviewModal: openReviewModal,
      ReviewModal: ReviewModalCallback,
    }),
    [openReviewModal, ReviewModalCallback]
  );
}
